import {
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import Flex from "@/components/Flex";
import { replaceUnderScoreAddSpace } from "@/utils/utils";
import {
  HOME_CARE_ROS_SAFETY_QUESTIONS,
  SafetyQuestion,
} from "../home-care-ros-data";
import {
  encodeHomeCareRosText,
  parseHomeCareRosAnswers,
  serializeHomeCareRosAnswers,
} from "../home-care-ros-utils";

export interface HomeCareRosSafetyAssessmentProps {
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

const splitValues = (answer?: string): string[] =>
  answer ? answer.split("|") : [];

const toggleMulti = (
  current: string[],
  option: string,
  isChecked: boolean,
  exclusiveOption?: string
): string[] => {
  if (!isChecked) return current.filter((item) => item !== option);
  if (option === exclusiveOption) return [option];
  return [...current.filter((item) => item !== exclusiveOption), option];
};

const HomeCareRosSafetyAssessment = ({
  value,
  onChange,
  disabled,
  readOnly,
}: HomeCareRosSafetyAssessmentProps) => {
  const answers = useMemo(() => parseHomeCareRosAnswers(value), [value]);

  const setAnswer = (key: string, answer: string) => {
    if (readOnly) return;
    onChange(serializeHomeCareRosAnswers({ ...answers, [key]: answer }));
  };

  const handleToggle = (
    question: SafetyQuestion,
    option: string,
    isChecked: boolean
  ) => {
    const current = splitValues(answers[question.key]);
    const next =
      question.type === "multi"
        ? toggleMulti(current, option, isChecked, question.exclusiveOption)
        : isChecked
          ? [option]
          : [];
    const cleared =
      question.type === "single" && option === "Yes" && !isChecked
        ? Object.fromEntries(
            (question.followUps ?? []).map((item) => [item.key, ""])
          )
        : {};
    if (readOnly) return;
    onChange(
      serializeHomeCareRosAnswers({
        ...answers,
        ...cleared,
        [question.key]: next.join("|"),
      })
    );
  };

  const renderQuestion = (question: SafetyQuestion) => {
    const label = replaceUnderScoreAddSpace(question.key);
    const selected = splitValues(answers[question.key]);
    const isYes = selected.includes("Yes");

    return (
      <Flex
        key={question.key}
        alignItems="center"
        flexWrap="wrap"
        columnGap={2}
      >
        <Typography variant="body2">{question.label}</Typography>
        {question.type === "text" ? (
          <TextField
            size="small"
            variant="standard"
            disabled={disabled}
            value={decodeURIComponent(answers[question.key] ?? "")}
            onChange={(e) =>
              setAnswer(question.key, encodeHomeCareRosText(e.target.value))
            }
            inputProps={{ "aria-label": label, readOnly }}
          />
        ) : (
          question.options?.map((option) => (
            <FormControlLabel
              key={option}
              label={replaceUnderScoreAddSpace(option)}
              disabled={disabled}
              control={
                <Checkbox
                  size="small"
                  checked={selected.includes(option)}
                  onChange={(_, checked) =>
                    handleToggle(question, option, checked)
                  }
                />
              }
            />
          ))
        )}
        {isYes && question.followUps?.map(renderQuestion)}
      </Flex>
    );
  };

  return (
    <Flex flexDirection="column" gap={0.5}>
      {HOME_CARE_ROS_SAFETY_QUESTIONS.map(renderQuestion)}
    </Flex>
  );
};

export default HomeCareRosSafetyAssessment;
